import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Clapperboard, Eye, EyeOff, Loader2, Mic, MonitorPlay, Play, Send, Square, Trash2, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { medienFeedbackAPI } from '@/lib/api';

/* Video- und Audio-Feedback (Coach-Cockpit W2.6, 29.10.2026). Aufnahme im Browser — „Kamera + Bildschirm“
   (getDisplayMedia + Kamera als Bild-in-Bild auf einem Canvas, MediaRecorder), „Nur Kamera“, „Nur Audio“ — mit Zähler,
   Stopp bei 3:00, Vorschau, Begleittext und Senden. Der Server prüft Typ und Dauer noch einmal (backend/medien_feedback).

   Props: `leadId`, `bezug` ('chat' | 'checkin'), `bezugId` (Check-in-ID bei 'checkin'), `client`
   ({ hochladen(formData, axiosCfg) → Promise }, z. B. `medienFeedbackClient(leadId)` oder auf /coach der Athlet-Pfad),
   `onGesendet(feedback)`, `kompakt` (nur ein Symbol-Knopf). Dazu `MedienFeedbackAbspieler` für den Verlauf und
   `MedienFeedbackEinstellung` (Aufbewahrung, nur Leitung). */

export const MAX_SEKUNDEN = 180;
const GRENZE_MB = { video: 60, bildschirm: 60, audio: 8 };
const MODI = [
  { key: 'bildschirm', typ: 'bildschirm', titel: 'Kamera + Bildschirm', text: 'Bildschirm teilen, du unten rechts im Bild', Icon: MonitorPlay },
  { key: 'kamera', typ: 'video', titel: 'Nur Kamera', text: 'Video von dir mit Ton', Icon: Video },
  { key: 'audio', typ: 'audio', titel: 'Nur Audio', text: 'Sprachaufnahme ohne Bild', Icon: Mic },
];
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const uhr = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const zeitpunkt = (iso) => { try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const endung = (typ) => (typ.includes('mp4') ? (typ.startsWith('audio') ? 'm4a' : 'mp4') : typ.includes('ogg') ? 'ogg' : 'webm');

// MP4 (H.264/AAC) zuerst: die App läuft meist auf iPhones, und AVPlayer spielt kein WebM. Chrome nimmt MP4 seit
// Version 126 auf, Safari immer; Firefox fällt auf WebM zurück.
const formatWahl = (audioOnly) => {
  const liste = audioOnly
    ? ['audio/mp4;codecs=mp4a.40.2', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg']
    : ['video/mp4;codecs=avc1.42E01E,mp4a.40.2', 'video/mp4;codecs=avc1,mp4a.40.2', 'video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return liste.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || '';
};

/** Client für CRM und Spiegel (/coach/x) — derselbe Umschalter wie die Ernährungskarten. */
export const medienFeedbackClient = (leadId) => ({
  hochladen: (fd, cfg) => medienFeedbackAPI.hochladen(leadId, fd, cfg),
  datei: (fid) => medienFeedbackAPI.datei(leadId, fid),
});

function takt(fps, tick) {
  /* Worker-Takt (läuft im Hintergrund-Tab weiter), sonst setInterval. Rückgabe: Stopp-Funktion. */
  try {
    const w = new Worker('/medien-takt.js');
    w.onmessage = tick;
    w.postMessage({ fps });
    return () => { try { w.postMessage({ fps: 0 }); w.terminate(); } catch { /* egal */ } };
  } catch {
    const id = setInterval(tick, Math.round(1000 / fps));
    return () => clearInterval(id);
  }
}

function abspielbar(stream) {
  const v = document.createElement('video');
  v.muted = true; v.playsInline = true; v.srcObject = stream;
  v.play().catch(() => {});
  return v;
}

export default function MedienFeedbackAufnahme({ leadId, bezug = 'chat', bezugId = null, client, onGesendet, kompakt = false, disabled = false }) {
  const [offen, setOffen] = useState(false);
  const [modus, setModus] = useState(null);
  const [phase, setPhase] = useState('wahl');            // wahl | laeuft | vorschau | senden
  const [sekunden, setSekunden] = useState(0);
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  const [text, setText] = useState('');
  const [fortschritt, setFortschritt] = useState(0);
  const live = useRef(null);
  const aufnahme = useRef({ recorder: null, streams: [], stoppTakt: null, timer: null, start: 0, dauer: 0, teile: [] });

  const aufraeumen = useCallback(() => {
    const a = aufnahme.current;
    clearInterval(a.timer); a.timer = null;
    if (a.stoppTakt) { a.stoppTakt(); a.stoppTakt = null; }
    a.streams.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    a.streams = [];
    if (live.current) live.current.srcObject = null;
  }, []);

  const stopp = useCallback(() => {
    const r = aufnahme.current.recorder;
    try { if (r && r.state !== 'inactive') r.stop(); } catch { /* egal */ }
  }, []);

  useEffect(() => () => { stopp(); aufraeumen(); }, [stopp, aufraeumen]);
  useEffect(() => { if (!blob) { setUrl(null); return undefined; } const u = URL.createObjectURL(blob); setUrl(u); return () => URL.revokeObjectURL(u); }, [blob]);

  const zuruecksetzen = () => { aufnahme.current.verworfen = true; stopp(); aufraeumen(); setBlob(null); setModus(null); setPhase('wahl'); setSekunden(0); setText(''); setFortschritt(0); };
  const schliessen = (v) => { if (!v) { if (phase === 'senden') return; zuruecksetzen(); } setOffen(v); };

  const starten = async (m) => {
    const md = navigator.mediaDevices;
    if (!md?.getUserMedia || typeof window.MediaRecorder === 'undefined') { toast.error('Aufnahme ist in diesem Browser nicht möglich.'); return; }
    if (m.key === 'bildschirm' && !md.getDisplayMedia) { toast.error('Bildschirm teilen geht in diesem Browser nicht — nimm „Nur Kamera“.'); return; }
    const a = aufnahme.current;
    let stream;
    try {
      if (m.key === 'audio') {
        stream = await md.getUserMedia({ audio: true });
        a.streams.push(stream);
      } else if (m.key === 'kamera') {
        stream = await md.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }, audio: true });
        a.streams.push(stream);
      } else {
        const schirm = await md.getDisplayMedia({ video: { frameRate: 30 }, audio: false });
        a.streams.push(schirm);
        let kamera = null;
        try { kamera = await md.getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' }, audio: true }); }
        catch {
          try { kamera = await md.getUserMedia({ audio: true }); toast.message('Kamera nicht freigegeben — nur Bildschirm und Ton.'); }
          catch { toast.message('Kamera und Mikrofon nicht freigegeben — nur der Bildschirm.'); }
        }
        if (kamera) a.streams.push(kamera);
        const sv = abspielbar(schirm);
        const kv = kamera && kamera.getVideoTracks().length ? abspielbar(kamera) : null;
        const s = schirm.getVideoTracks()[0]?.getSettings?.() || {};
        const breite = Math.min(1280, s.width || 1280);
        const hoehe = Math.round(breite * ((s.height || 720) / (s.width || 1280)));
        const canvas = document.createElement('canvas');
        canvas.width = breite; canvas.height = hoehe;
        const ctx = canvas.getContext('2d');
        const zeichnen = () => {
          ctx.fillStyle = '#000'; ctx.fillRect(0, 0, breite, hoehe);
          if (sv.videoWidth) {
            const f = Math.min(breite / sv.videoWidth, hoehe / sv.videoHeight);
            const w = sv.videoWidth * f; const h = sv.videoHeight * f;
            ctx.drawImage(sv, (breite - w) / 2, (hoehe - h) / 2, w, h);
          }
          if (kv && kv.videoWidth) {           // Bild-in-Bild unten rechts, abgerundet, mit hellem Rand
            const w = Math.round(breite * 0.24); const h = Math.round(w * (kv.videoHeight / kv.videoWidth));
            const x = breite - w - 16; const y = hoehe - h - 16; const r = 14;
            ctx.save(); ctx.beginPath();
            if (ctx.roundRect) ctx.roundRect(x, y, w, h, r); else ctx.rect(x, y, w, h);
            ctx.clip(); ctx.drawImage(kv, x, y, w, h); ctx.restore();
            ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,0.85)';
            ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(x, y, w, h, r); else ctx.rect(x, y, w, h); ctx.stroke();
          }
        };
        a.stoppTakt = takt(30, zeichnen);
        stream = canvas.captureStream(30);
        (kamera?.getAudioTracks() || []).forEach((t) => stream.addTrack(t));
        schirm.getVideoTracks()[0]?.addEventListener('ended', () => stopp());   // „Freigabe beenden“ im Browser
      }
    } catch {
      aufraeumen();
      toast.error(m.key === 'bildschirm' ? 'Bildschirm oder Kamera nicht freigegeben.' : m.key === 'audio' ? 'Mikrofon nicht freigegeben.' : 'Kamera nicht freigegeben.');
      return;
    }
    const mimeType = formatWahl(m.key === 'audio');
    let rec;
    try {
      rec = new window.MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), ...(m.key === 'audio' ? {} : { videoBitsPerSecond: 1_500_000 }), audioBitsPerSecond: 96_000 });
    } catch { aufraeumen(); toast.error('Aufnahme konnte nicht starten.'); return; }
    a.teile = [];
    rec.ondataavailable = (e) => { if (e.data?.size) a.teile.push(e.data); };
    a.verworfen = false;
    rec.onstop = () => {
      if (a.verworfen) { aufraeumen(); return; }       // Dialog geschlossen oder verworfen: nichts übernehmen
      a.dauer = Math.min(MAX_SEKUNDEN, (Date.now() - a.start) / 1000);
      aufraeumen();
      const typ = (rec.mimeType || mimeType || (m.key === 'audio' ? 'audio/webm' : 'video/webm')).split(';')[0];
      const b = new Blob(a.teile, { type: typ });
      if (b.size < 1000 || a.dauer < 1) { toast.error('Die Aufnahme ist zu kurz.'); setPhase('wahl'); setModus(null); return; }
      setBlob(b); setPhase('vorschau');
    };
    a.recorder = rec;
    rec.start(1000);
    a.start = Date.now();
    setModus(m); setSekunden(0); setPhase('laeuft');
    if (live.current && m.key !== 'audio') { live.current.srcObject = stream; live.current.play?.().catch(() => {}); }
    a.timer = setInterval(() => {
      const s = Math.floor((Date.now() - a.start) / 1000);
      setSekunden(s);
      if (s >= MAX_SEKUNDEN) stopp();
    }, 250);
  };

  useEffect(() => {      // Live-Bild nach dem Umschalten auf „läuft“ (das <video> entsteht erst dann)
    if (phase === 'laeuft' && live.current && !live.current.srcObject) {
      const s = aufnahme.current.recorder?.stream;
      if (s && s.getVideoTracks().length) { live.current.srcObject = s; live.current.play?.().catch(() => {}); }
    }
  }, [phase]);

  const senden = async () => {
    if (!blob || !modus || !client?.hochladen) return;
    const mb = blob.size / (1024 * 1024);
    if (mb > GRENZE_MB[modus.typ]) { toast.error(`Die Aufnahme ist zu groß (${mb.toFixed(0)} MB, höchstens ${GRENZE_MB[modus.typ]} MB) — bitte kürzer aufnehmen.`); return; }
    const fd = new FormData();
    fd.append('datei', new File([blob], `feedback.${endung(blob.type)}`, { type: blob.type }));
    fd.append('typ', modus.typ);
    fd.append('bezug', bezug);
    if (bezugId) fd.append('bezug_id', bezugId);
    fd.append('dauer_s', String(Math.round(aufnahme.current.dauer)));
    if (text.trim()) fd.append('text', text.trim());
    setPhase('senden'); setFortschritt(0);
    try {
      const r = await client.hochladen(fd, { onUploadProgress: (e) => { if (e.total) setFortschritt(Math.round((e.loaded / e.total) * 100)); } });
      toast.success('Feedback gesendet — das Mitglied bekommt eine Mitteilung.');
      onGesendet?.(r?.data);
      zuruecksetzen(); setOffen(false);
    } catch (e) { toast.error(fehlertext(e, 'Nicht gesendet')); setPhase('vorschau'); }
  };

  const ausloeser = kompakt
    ? <Button type="button" size="icon" variant="outline" className="h-11 w-11 shrink-0" aria-label="Video- oder Audio-Feedback aufnehmen" title="Video-/Audio-Feedback" onClick={() => setOffen(true)} disabled={disabled} data-testid="medien-feedback-knopf"><Clapperboard className="h-4 w-4" /></Button>
    : <Button type="button" variant="outline" className="h-11 gap-1.5" onClick={() => setOffen(true)} disabled={disabled} data-testid="medien-feedback-knopf"><Clapperboard className="h-4 w-4" />Video-Feedback</Button>;

  return (
    <>
      {ausloeser}
      <Dialog open={offen} onOpenChange={schliessen}>
        <DialogContent className="max-w-xl" data-testid="medien-feedback-aufnahme" data-lead={leadId || undefined}>
          <DialogHeader>
            <DialogTitle>{bezug === 'checkin' ? 'Feedback zum Check-in aufnehmen' : 'Video- oder Audio-Feedback'}</DialogTitle>
            <DialogDescription>Höchstens 3 Minuten. Das Mitglied sieht es in der App im Chat; du siehst, wann es angesehen wurde. Gelöscht wird es automatisch nach der Aufbewahrungsfrist.</DialogDescription>
          </DialogHeader>

          {phase === 'wahl' && (
            <div className="grid gap-2 sm:grid-cols-3">
              {MODI.map((m) => (
                <button key={m.key} type="button" onClick={() => starten(m)} data-testid={`medien-modus-${m.key}`}
                  className="flex min-h-[44px] flex-col items-start gap-1 rounded-xl border bg-card p-3 text-left text-sm transition hover:border-marke focus-visible:outline focus-visible:outline-2 focus-visible:outline-marke">
                  <m.Icon className="h-5 w-5 text-marke" aria-hidden="true" />
                  <span className="font-semibold">{m.titel}</span>
                  <span className="text-xs text-muted-foreground">{m.text}</span>
                </button>
              ))}
            </div>
          )}

          {phase === 'laeuft' && (
            <div className="space-y-3">
              {modus?.key !== 'audio'
                ? <video ref={live} muted playsInline autoPlay className="aspect-video w-full rounded-xl bg-black object-contain" />
                : <div className="flex h-24 items-center justify-center rounded-xl bg-muted"><Mic className="h-8 w-8 animate-pulse text-marke" aria-hidden="true" /></div>}
              <div className="flex items-center gap-3">
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-marke" aria-hidden="true" />
                <span className="font-mono text-lg tabular-nums" aria-live="polite">{uhr(sekunden)} / {uhr(MAX_SEKUNDEN)}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full bg-marke transition-[width]" style={{ width: `${Math.min(100, (sekunden / MAX_SEKUNDEN) * 100)}%` }} /></div>
                <Button type="button" className="h-11 gap-1.5" onClick={stopp} data-testid="medien-stopp"><Square className="h-4 w-4" />Stopp</Button>
              </div>
            </div>
          )}

          {(phase === 'vorschau' || phase === 'senden') && url && (
            <div className="space-y-3">
              {modus?.typ === 'audio'
                ? <audio controls src={url} className="w-full" />
                : <video controls playsInline src={url} className="aspect-video w-full rounded-xl bg-black object-contain" />}
              <p className="text-xs text-muted-foreground">{modus?.titel} · {uhr(aufnahme.current.dauer)} · {(blob.size / (1024 * 1024)).toFixed(1)} MB</p>
              <label className="block text-sm" htmlFor="medien-feedback-text">Begleittext (freiwillig)</label>
              <Input id="medien-feedback-text" maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="z. B. Schau dir die Hüfte bei der Kniebeuge an" className="h-11" disabled={phase === 'senden'} />
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="ghost" className="h-11 gap-1.5" onClick={zuruecksetzen} disabled={phase === 'senden'}><Trash2 className="h-4 w-4" />Verwerfen</Button>
                <span className="ml-auto text-xs text-muted-foreground">{phase === 'senden' ? `Lädt hoch … ${fortschritt} %` : ''}</span>
                <Button type="button" className="h-11 gap-1.5" onClick={senden} disabled={phase === 'senden'} data-testid="medien-senden">{phase === 'senden' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Senden</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Feedback im Verlauf: lädt erst auf Klick (Videos sind groß), zeigt „angesehen am …“. `datei(fid)` → axios-Blob. */
export function MedienFeedbackAbspieler({ medien, datei, hell = false }) {
  const [url, setUrl] = useState(null);
  const [laedt, setLaedt] = useState(false);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const leise = hell ? 'text-white/80' : 'text-muted-foreground';
  const titel = medien.typ === 'audio' ? 'Audio-Feedback' : medien.typ === 'bildschirm' ? 'Video-Feedback mit Bildschirm' : 'Video-Feedback';
  const stand = medien.angesehen_am
    ? <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" aria-hidden="true" />angesehen am {zeitpunkt(medien.angesehen_am)}</span>
    : <span className="inline-flex items-center gap-1"><EyeOff className="h-3 w-3" aria-hidden="true" />noch nicht angesehen</span>;
  if (medien.geloescht) return <p className={`text-xs ${leise}`} data-testid="medien-geloescht">{titel} · nicht mehr verfügbar (Aufbewahrungsfrist)</p>;
  const laden = async () => {
    setLaedt(true);
    try { const r = await datei(medien.id); setUrl(URL.createObjectURL(r.data)); }
    catch (e) { toast.error(fehlertext(e, 'Konnte nicht geladen werden')); }
    finally { setLaedt(false); }
  };
  return (
    <div className="space-y-1" data-testid={`medien-${medien.id}`}>
      {url
        ? (medien.typ === 'audio' ? <audio controls autoPlay src={url} className="h-9 w-full max-w-xs" /> : <video controls autoPlay playsInline src={url} className="max-h-72 w-full max-w-sm rounded-lg bg-black" />)
        : <button type="button" onClick={laden} disabled={laedt} className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm font-medium ${hell ? 'border-white/40 bg-white/10' : 'bg-muted'}`}>
            {laedt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}{titel}{medien.dauer_s ? ` · ${uhr(medien.dauer_s)}` : ''}
          </button>}
      <p className={`text-[11px] ${leise}`}>{stand}</p>
    </div>
  );
}

/** Aufbewahrung der Feedbacks (Monate) — ändern nur die Leitung, der Server prüft. */
export function MedienFeedbackEinstellung() {
  const [d, setD] = useState(null);
  const [monate, setMonate] = useState('');
  const [busy, setBusy] = useState(false);
  const laden = useCallback(() => medienFeedbackAPI.einstellung().then((r) => { setD(r.data); setMonate(String(r.data.aufbewahrung_monate)); }).catch(() => {}), []);
  useEffect(() => { laden(); }, [laden]);
  if (!d) return null;
  const speichern = async () => {
    setBusy(true);
    try { const r = await medienFeedbackAPI.einstellungSetzen(Number(monate)); toast.success(`Gespeichert — ${r.data.neu_gerechnet} Feedbacks neu gerechnet`); await laden(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="mt-4" data-testid="medien-feedback-einstellung">
      <CardHeader className="pb-2"><CardTitle className="text-base">Video-/Audio-Feedback</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-xs text-muted-foreground">Aufnahmen des Coachs an Mitglieder (höchstens {uhr(d.max_sekunden)} Minuten) werden nach dieser Frist automatisch gelöscht; im Chat bleibt der Vermerk. Eine Änderung gilt auch für schon gesendete Feedbacks.</p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm" htmlFor="medien-aufbewahrung">Aufbewahrung in Monaten (1–36)</label>
          <Input id="medien-aufbewahrung" type="number" min={1} max={36} value={monate} onChange={(e) => setMonate(e.target.value)} className="h-11 w-24" />
          <Button className="h-11" disabled={busy || Number(monate) === d.aufbewahrung_monate} onClick={speichern}>Speichern</Button>
        </div>
        {d.geaendert_von && <p className="text-xs text-muted-foreground">Zuletzt geändert von {d.geaendert_von}</p>}
      </CardContent>
    </Card>
  );
}
